// Full monthly calendar grid for the Forecasting tab.
// Supports inline editing: the selected day shows DDS/HYG inputs directly in the cell
// so users don't need to scroll down to the form below.
import { useState, useEffect } from "react";
import { useCreateForecast, useUpdateForecast } from "@api/hooks";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { parseNumber } from "@utils/format";
import { torontoToday } from "@utils";
import { toast } from "react-hot-toast";
// Direct relative import to avoid a circular dependency — both Button and ForecastCalendar
// are exported from @common/index.js, so importing Button via @common would create a cycle.
import Button from "./Button";

// Week starts on Monday — Sunday is placed at the right end
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Formats a number without $ for calendar cells.
// Shows up to 2 decimal places when they exist (e.g. 13,197.26) but omits trailing
// zeros on round numbers (e.g. 190,000 not 190,000.00) to keep cells readable.
function fmt(value) {
  return parseFloat(value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

// Converts year/month/day integers to a YYYY-MM-DD string for map lookups and comparisons
function toDateStr(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

// Returns an array of cells for the grid: null for empty leading slots, then 1-N day numbers.
// Uses (getDay() + 6) % 7 to convert JS's Sun=0 to Mon=0 so the week starts on Monday.
function buildCells(year, month) {
  const jsDow = new Date(year, month, 1).getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  const firstDow = (jsDow + 6) % 7; // convert to Mon=0 ... Sun=6
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  return [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
}

// ─── Local helper components ────────────────────────────────────────────────
//
// These live inside this file intentionally. They are purely presentational
// building-blocks for the calendar cell layout and have no meaning outside it.
// Exporting them to @common would add noise to the shared barrel for no benefit.

// Small colored chip: label on top, value below.
// Used for DDS FCT, HYG FCT, ACT DDS, ACT HYG, ACTUAL, and legacy FCT chips.
// bg: Tailwind background class (e.g. "bg-palette-indigo-soft")
// labelColor: Tailwind text color for the label row (e.g. "text-palette-indigo-label")
function Chip({ label, value, bg, labelColor }) {
  return (
    <div className={`rounded px-1 py-0.5 ${bg}`}>
      <div className={`text-[10px] font-bold ${labelColor} leading-none`}>{label}</div>
      <div className="text-xs font-semibold text-palette-gray-heading truncate leading-tight">
        {value}
      </div>
    </div>
  );
}

// Horizontal chip for the "TOT FCT" total — label on the left, value on the right.
// Appears both in the inline edit form (after the Save button) and the static read-only display.
function TotChip({ value }) {
  return (
    <div className="rounded px-1 py-0.5 bg-palette-gray-subtle flex items-center justify-between gap-1">
      <span className="text-[10px] font-bold text-palette-gray-secondary shrink-0">TOT FCT</span>
      <span className="text-xs font-bold text-palette-gray-text truncate">{value}</span>
    </div>
  );
}

// Compact number input with a tiny colored label above it.
// Used for the DDS and HYG inline entry fields inside a selected calendar cell.
// Each color prop is a full Tailwind class string so the two providers can use different colors.
function CellInput({ label, labelColor, borderColor, ringColor, value, onChange }) {
  return (
    <div>
      <div className={`text-[10px] font-bold ${labelColor} mb-0.5`}>{label}</div>
      <input
        type="number"
        min="0"
        className={`w-full h-7 text-xs border ${borderColor} rounded px-1.5 bg-white focus:outline-none focus:ring-1 ${ringColor}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0"
      />
    </div>
  );
}

// Circular ‹ / › arrow button used in the month navigation header.
// Kept as a local helper because the circular styling is calendar-specific
// and doesn't match the standard Button variants in @common.
function NavButton({ onClick, ariaLabel, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-palette-gray-subtle transition-colors text-palette-gray-badge"
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function ForecastCalendar({
  year,
  month,
  forecastsByDate = {},
  selectedDate,
  onDayClick,
  onPrevMonth,
  onNextMonth,
  maxDate,
  clinic, // needed for inline create/update API calls
  canEdit = true,
}) {
  const cells = buildCells(year, month);
  // Pad trailing nulls so the last row always has 7 cells (avoids a short final row)
  const padded = [...cells];
  while (padded.length % 7 !== 0) padded.push(null);

  const today = torontoToday();

  // Inline edit state — DDS and HYG inputs for the currently selected day
  const [editDds, setEditDds] = useState("");
  const [editHyg, setEditHyg] = useState("");

  const createForecastMutation = useCreateForecast();
  const updateForecastMutation = useUpdateForecast();
  const inlinePending = createForecastMutation.isPending || updateForecastMutation.isPending;

  // Pre-fill inputs whenever the selected date or its cached forecast changes
  useEffect(() => {
    const f = selectedDate ? forecastsByDate[selectedDate] : null;
    setEditDds(f?.forecast_dds != null ? String(f.forecast_dds) : "");
    setEditHyg(f?.forecast_hyg != null ? String(f.forecast_hyg) : "");
  }, [selectedDate, forecastsByDate]);

  async function handleInlineSave(dateStr, existingForecast) {
    // Check for a negative sign BEFORE parseNumber strips it — "-77" would otherwise
    // silently become 77 and be saved without any warning to the user.
    if (editDds.trim().startsWith("-") || editHyg.trim().startsWith("-")) {
      toast.error("Forecast values cannot be negative");
      return;
    }
    const dds = parseNumber(editDds);
    const hyg = parseNumber(editHyg);
    if (dds <= 0 && hyg <= 0) return;

    try {
      const data = { clinic, date: dateStr, forecast_dds: dds, forecast_hyg: hyg };
      // Check existingForecast?.id (not just existingForecast) because placeholder rows
      // injected by the backend for days with actuals-but-no-forecast have id: null.
      // A truthy-only check would mistake a placeholder for a real saved record.
      if (existingForecast?.id) {
        await updateForecastMutation.mutateAsync({ id: existingForecast.id, data });
        toast.success("Forecast updated!");
      } else {
        await createForecastMutation.mutateAsync(data);
        toast.success("Forecast saved!");
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err) || "Failed to save forecast");
    }
  }

  return (
    <div>
      {/* Month navigation header */}
      <div className="flex items-center justify-between mb-4">
        <NavButton onClick={onPrevMonth} ariaLabel="Previous month">
          ‹
        </NavButton>
        <h3 className="text-base font-semibold text-palette-gray-text">
          {MONTH_NAMES[month]} {year}
        </h3>
        <NavButton onClick={onNextMonth} ariaLabel="Next month">
          ›
        </NavButton>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 mb-1">
        {DAY_LABELS.map((d) => (
          <div
            key={d}
            className="text-center text-xs font-medium text-palette-gray-placeholder py-1"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {padded.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} />;

          const dateStr = toDateStr(year, month, day);
          const forecast = forecastsByDate[dateStr];
          const isSelected = selectedDate === dateStr;
          const isToday = dateStr === today;
          // isBeyondLimit: too far in the future — completely greyed out and unclickable.
          //   Used to prevent entering forecasts further ahead than the allowed max date.
          const isBeyondLimit = maxDate ? dateStr > maxDate : false;
          // isHistorical: already happened (before today) — still clickable and readable,
          //   but the inline DDS/HYG edit form is hidden since past forecasts cannot be changed
          //   from the calendar cell (they can still be edited via the right-panel form).
          const isHistorical = dateStr < today;
          const hasForecast = !!forecast;

          const dds = parseNumber(editDds);
          const hyg = parseNumber(editHyg);
          const canSave = (dds > 0 || hyg > 0) && !inlinePending;

          return (
            // Using div instead of button so <input> elements can be nested inside
            <div
              key={dateStr}
              role="button"
              tabIndex={isBeyondLimit ? -1 : 0}
              onClick={() => !isBeyondLimit && onDayClick?.(dateStr)}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && !isBeyondLimit) onDayClick?.(dateStr);
              }}
              className={[
                "relative flex flex-col rounded-lg p-1.5 min-h-[130px] transition-all border",
                isBeyondLimit
                  ? "opacity-40 cursor-not-allowed border-transparent"
                  : "cursor-pointer hover:border-palette-indigo-hover hover:bg-palette-indigo-soft",
                isSelected
                  ? "border-palette-indigo-label bg-palette-indigo-soft ring-2 ring-palette-indigo-ring ring-offset-1"
                  : "border-palette-gray-divider",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {/* Date number — top-right, highlighted circle on today */}
              <span
                className={[
                  "w-6 h-6 flex items-center justify-center rounded-full text-xs font-semibold self-end mb-1",
                  isToday ? "bg-palette-indigo-badge text-white" : "text-palette-gray-heading",
                ].join(" ")}
              >
                {day}
              </span>

              {/*
                SELECTED DAY — today or future + user has edit permission:
                show DDS/HYG inputs + Save/Update button directly in the cell.
                Past dates fall through to the static read-only display below.
              */}
              {isSelected && canEdit && !isHistorical ? (
                <div
                  className="w-full space-y-1"
                  // Prevent clicks inside the form from bubbling up to the cell's onClick
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <CellInput
                    label="DDS"
                    labelColor="text-palette-indigo-label"
                    borderColor="border-palette-indigo-border"
                    ringColor="focus:ring-palette-indigo-ring"
                    value={editDds}
                    onChange={setEditDds}
                  />
                  <CellInput
                    label="HYG"
                    labelColor="text-palette-emerald-badge"
                    borderColor="border-palette-emerald-border"
                    ringColor="focus:ring-palette-emerald-ring"
                    value={editHyg}
                    onChange={setEditHyg}
                  />
                  {/* Save button — label is "Update" only when a real record exists (id present),
                      "Save" for new entries AND placeholder rows (id: null from the backend) */}
                  <Button
                    type="button"
                    variant="primary"
                    size="xs"
                    block
                    onClick={() => handleInlineSave(dateStr, forecast)}
                    disabled={!canSave}
                  >
                    {inlinePending ? "Saving..." : forecast?.id ? "Update" : "Save"}
                  </Button>
                  {/* TOT FCT context — only shown when a real saved record exists (id present) */}
                  {forecast?.id && <TotChip value={fmt(parseFloat(forecast.forecast ?? 0))} />}
                </div>
              ) : hasForecast ? (
                /*
                  ALL OTHER DAYS with a saved forecast — static read-only display.
                  Layout: [DDS FCT / HYG FCT] left  |  [ACT DDS / ACT HYG] right
                           TOT FCT spanning full width at the bottom.
                */
                <div className="w-full space-y-1">
                  <div className="flex gap-1">
                    {/* Left column: planned forecast chips */}
                    <div className="flex-1 min-w-0 space-y-0.5">
                      {forecast.forecast_dds != null && (
                        <Chip
                          label="DDS FCT"
                          value={fmt(forecast.forecast_dds)}
                          bg="bg-palette-indigo-soft"
                          labelColor="text-palette-indigo-label"
                        />
                      )}
                      {forecast.forecast_hyg != null && (
                        <Chip
                          label="HYG FCT"
                          value={fmt(forecast.forecast_hyg)}
                          bg="bg-palette-emerald-soft"
                          labelColor="text-palette-emerald-badge"
                        />
                      )}
                      {/* Legacy: old records that only have a single combined forecast total (no DDS/HYG split) */}
                      {forecast.forecast_dds == null &&
                        forecast.forecast_hyg == null &&
                        forecast.forecast != null && (
                          <Chip
                            label="FCT"
                            value={fmt(forecast.forecast)}
                            bg="bg-palette-indigo-soft"
                            labelColor="text-palette-indigo-label"
                          />
                        )}
                    </div>

                    {/* Right column: actual production chips injected by views.py _get_daily_split.
                        mtd_dds/mtd_hyg = that day's actual production only (not cumulative MTD).
                        Math.max(0, ...) guards against rare negative values in the report data. */}
                    <div className="flex-1 min-w-0 space-y-0.5">
                      {forecast.mtd_dds != null && (
                        <Chip
                          label="ACT DDS"
                          value={fmt(Math.max(0, forecast.mtd_dds))}
                          bg="bg-palette-indigo-soft"
                          labelColor="text-palette-indigo-label"
                        />
                      )}
                      {forecast.mtd_hyg != null && (
                        <Chip
                          label="ACT HYG"
                          value={fmt(Math.max(0, forecast.mtd_hyg))}
                          bg="bg-palette-emerald-soft"
                          labelColor="text-palette-emerald-badge"
                        />
                      )}
                      {/* Legacy fallback: records fetched before the DDS/HYG split was added */}
                      {forecast.mtd_dds == null &&
                        forecast.mtd_hyg == null &&
                        forecast.mtd_production != null && (
                          <Chip
                            label="ACTUAL"
                            value={fmt(forecast.mtd_production)}
                            bg="bg-palette-amber-soft"
                            labelColor="text-palette-amber-badge"
                          />
                        )}
                    </div>
                  </div>

                  {/* TOT FCT spanning full width at the bottom — the combined DDS+HYG forecast total */}
                  {forecast.forecast != null && (
                    <TotChip value={fmt(parseFloat(forecast.forecast ?? 0))} />
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
